import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUser } from '../../UserContext';
import { Card, CardContent, Typography, Button, Grid, Tabs, Tab, Box, Chip, Dialog, DialogTitle, DialogContent, DialogActions } from '@mui/material';
import { getOrders, createUpdateOrder, acceptOrder } from '../../services/orderService';
import { getKitchensByChef } from '../../services/kitchenService';

// mui
import LocalShippingOutlinedIcon from '@mui/icons-material/LocalShippingOutlined';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import BookmarkBorderIcon from '@mui/icons-material/BookmarkBorder';
import QueryBuilderOutlinedIcon from '@mui/icons-material/QueryBuilderOutlined';
import Alert from '@mui/material/Alert';
import { OrderStatus } from '../../enums/OrderStatuses';
import { useAuth } from '../../AuthContext';
import hubWebSocketService from '../../services/hubWebSocketService';
import { useOrderEvents } from '../shared/OrderEventsContext';
import { useSnackbar } from '../shared/SnackbarContext';
import PersonIcon from '@mui/icons-material/Person';
import CallIcon from '@mui/icons-material/Call';

import { getHubMessageFromJson } from '../../utils/hubCommonFunctions';
import { Utils } from '../../utils/utils';
import CustomerInformation from './CustomerInformation';
import ChefContactInformation from './ChefContactInformation';
import { useKitchen } from '../../contexts/KitchenContext';

export default function OrderOverview(props) {
  const { user } = useUser();
  const { auth } = useAuth();
  const navigate = useNavigate();
  const userTypeLabel = Utils.isChef(user) ? 'chef' : 'customer';
  const { currentKitchenData } = useKitchen();
  const [ orders, setOrders ] = useState([]);
  const [orderTimes, setOrderTimes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const { refreshKey, triggerRefreshOrders } = useOrderEvents();
  const { showMessage } = useSnackbar();
  const [selectedStatus, setSelectedStatus] = useState(null);
  const [availableStatuses, setAvailableStatuses] = useState([]);
  const [kitchenExists, setKitchenExists] = useState(false);
  const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
  const [pendingAction, setPendingAction] = useState(null);
  const [pendingOrder, setPendingOrder] = useState(null);

  function requiresConfirmation(actionOption) {
    const normalizedOption = actionOption?.toLowerCase();
    return normalizedOption === 'cancel' || normalizedOption === 'reject';
  }

  function getConfirmationActionVerb(actionOption) {
    const normalizedOption = actionOption?.toLowerCase();
    if (normalizedOption === 'reject') return 'reject';
    return 'cancel';
  }

  // Define status groups
  const statusGroups = {
    'Active': [OrderStatus.Placed, 
               OrderStatus.Accepted, 
               OrderStatus.Paid,
               OrderStatus.InProgress, 
               OrderStatus.ReadyToPickup, 
               OrderStatus.OutForDelivery, 
               OrderStatus.Delivered, 
               OrderStatus.PickedUp],
    'Completed': [OrderStatus.Completed],
    'Cancelled': [OrderStatus.Cancelled, 
                  OrderStatus.Abandonned, 
                  OrderStatus.Lost, 
                  OrderStatus.Rejected]
  };

  const updateOrderTimes = (orders) => {
    if (!orders?.length) return;
    
    const now = new Date();
    const updatedOrderTimes = orders.map(order => {
      const dateOfReference = new Date(getLatestStatus(order)?.statusDate || order.orderDate);
      const diffTime = Math.abs(now - dateOfReference);
      
      // Calculate time components
      const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
      const diffHours = Math.floor((diffTime % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      const diffMinutes = Math.floor((diffTime % (1000 * 60 * 60)) / (1000 * 60));

      // Format the time difference nicely
      let difference;
      if (diffDays > 0) {
        difference = `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
      } else if (diffHours > 0) {
        difference = `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
      } else if (diffMinutes > 0) {
        difference = `${diffMinutes} minute${diffMinutes > 1 ? 's' : ''} ago`;
      } else {
        difference = 'Just now';
      }

      return {
        id: order.id,
        difference: difference
      };
    });

    setOrderTimes(updatedOrderTimes);
  };

  function getLatestStatus(order) {
    if (order.history?.length > 0) {
      return order.history.find(h => h.status === order.status);
    }

    return null;
  }

  useEffect(() => {
    const getOrdersList = async () => {
        try {
            let orderList = [];
            if (user.chef) {
              if (currentKitchenData?.id > 0) {
                setKitchenExists(true);
                orderList = await getOrders({ criteria: 'kitchen', id: currentKitchenData.id}, auth);
              } else {
                setKitchenExists(false);
              }
            } else if (user.customer) {
              orderList = await getOrders({ criteria: 'customer', id: user.customer.id}, auth);
            }

            setOrders(orderList);

            if (orderList?.length > 0) {
              updateOrderTimes(orderList);
              
              // Find which groups have orders
              const availableGroups = Object.keys(statusGroups).filter(group => 
                orderList.some(order => statusGroups[group].includes(order.status))
              );
              
              setAvailableStatuses(availableGroups);
              
              // Set default selected group: first available group
              if (availableGroups.length > 0) {
                setSelectedStatus(availableGroups[0]);
              }
            }
        } catch (error) {
            setError(error.message);
        } finally {
            setLoading(false);
        }
    };

    getOrdersList();
    
  }, [refreshKey]);

  // Separate useEffect for updating order times every minute
  useEffect(() => {
    if (orders?.length > 0) {
      // Set up interval to update times every minute
      const interval = setInterval(() => {
        updateOrderTimes(orders);
      }, 60000); // Update every minute (60000ms)
      
      return () => clearInterval(interval); // Clean up the interval on component unmount
    }
  }, [orders]); // Re-run when orders change


  function isOrderInFlight(order) {
    return order.status === OrderStatus.Placed ||
           order.status === OrderStatus.Accepted ||
           order.status === OrderStatus.InProgress ||
           order.status === OrderStatus.ReadyToPickup ||
           order.status === OrderStatus.OutForDelivery ||
           order.status === OrderStatus.Delivered ||
           order.status === OrderStatus.PickedUp;
  }

  function getDisplayName(status) {
    if (status === OrderStatus.InProgress) return 'In Progress';
    if (status === OrderStatus.ReadyToPickup) return 'Ready to Pickup';
    if (status === OrderStatus.OutForDelivery) return 'Out for Delivery';
    return status;
  }

  function getStatusChipColor(status) {
    if (status === OrderStatus.Completed) return 'success';
    if (
      status === OrderStatus.Cancelled ||
      status === OrderStatus.Abandonned ||
      status === OrderStatus.Lost ||
      status === OrderStatus.Rejected
    ) {
      return 'error';
    }
    return 'info';
  }

  function formatPaymentPreference(value) {
    if (value === 'atPickup') return 'Pay at pickup';
    if (value === 'upfront') return 'Upfront';
    return 'Not specified';
  }

  function formatPaymentMethod(value) {
    if (!value || typeof value !== 'string') {
      return 'Not specified';
    }

    const methodLabels = {
      payment_apps: 'Payment Apps',
      cash: 'Cash',
    };

    const uniqueMethods = [...new Set(
      value
        .split(',')
        .map((method) => method.trim())
        .map((method) => (method === 'paymentApps' ? 'payment_apps' : method))
        .filter(Boolean)
    )];

    if (uniqueMethods.length === 0) {
      return 'Not specified';
    }

    return uniqueMethods
      .map((method) => methodLabels[method] || method)
      .join(', ');
  }

  function sortOrdersByDateDesc(orderList) {
    return [...(orderList || [])].sort((a, b) => {
      const aDate = new Date(a.orderDate || 0).getTime();
      const bDate = new Date(b.orderDate || 0).getTime();
      return bDate - aDate;
    });
  }

  function getUpfrontPaymentMessage(order) {
    if (needsPaymentMessage(order)) {
      if (Utils.isChef(user)) {
        return 'Waiting for customer payment.';
      }

      return 'Please make a direct payment to the chef so the order can proceed.';
    }
  }

  function needsPaymentMessage(order) {
    const isUpfrontPayment = order?.paymentPreference === 'upfront';
    const isWaitingForPayment = order?.status === OrderStatus.Accepted;

    if (isUpfrontPayment && isWaitingForPayment) {
      return true;
    }

    return false;
  }

  const handleConfirmCancelOrder = async () => {
    if (pendingAction && pendingOrder) {
      if (pendingAction.statusAssociated) {
        await handleStatus(pendingAction.statusAssociated, pendingOrder);
      } else {
        // Handle non-status actions if needed
        // await handleAction(pendingAction.option, pendingOrder);
      }
    }
    setOpenConfirmDialog(false);
    setPendingAction(null);
    setPendingOrder(null);
  };

  const handleCancelConfirmDialog = () => {
    setOpenConfirmDialog(false);
    setPendingAction(null);
    setPendingOrder(null);
  };

  const handleOrderAction = (action, order) => {
    if (requiresConfirmation(action.option)) {
      setPendingAction(action);
      setPendingOrder(order);
      setOpenConfirmDialog(true);
    } else {
      // For non-cancel actions, proceed immediately
      if (action.statusAssociated) {
        handleStatus(action.statusAssociated, order);
      } else {
        handleAction(action.option, order);
      }
    }
  };

  const handleAction = async (action, order) => {
    // to code...
  };

  function refreshOrderList(updatedOrder) {
    const list = [];
    Object.assign(list, orders);

    const order = list.find(order => order.id === updatedOrder.id)
    list.splice(list.indexOf(order), 1, updatedOrder);

    setOrders(list);
  }
  
  const handleStatus = async (action, order) => {
    setLoading(true);
    let currentStatus = order.status;

    try {
      order.status = action;
      let orderUpdateResult;
      let notificationMessage = `Order ${order.orderNumber} ${action}.`;

      if (action === OrderStatus.Accepted) {
        notificationMessage = `Order ${order.orderNumber} has been accepted.`;
      }

      orderUpdateResult = await createUpdateOrder(order, auth);

      // notify customer or chef based on the user type
      hubWebSocketService.sendMessage(JSON.stringify({
        to: Utils.isChef(user) ? 
        `cus-${order.customerId}` : 
        `chef-${order.kitchenId}`,
        content: notificationMessage
      }));

      triggerRefreshOrders();
      //refreshOrderList(orderUpdateResult);  // only client side update
      
    } catch (error) {
      order.status = currentStatus;
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleStatusFilterChange = (event, newValue) => {
    setSelectedStatus(newValue);
  };

  // Filter orders based on selected group
  const filteredOrders = selectedStatus && statusGroups[selectedStatus] 
    ? sortOrdersByDateDesc(orders.filter(order => statusGroups[selectedStatus].includes(order.status)))
    : [];

  if (!loading && user.chef && !kitchenExists) {
    return (
      <Box sx={{ textAlign: 'center', padding: 4 }}>
        <Typography variant="h6" gutterBottom>
          You don't have a kitchen set up yet.
        </Typography>
        <Button 
          variant="contained" 
          color="primary"
          onClick={() => navigate('/dashboard')}
          style={{textTransform: 'none'}}
          sx={{ marginTop: 2 }}
        >
          Create Your Kitchen
        </Button>
      </Box>
    );
  }

  return (
    <div>
      {/* Status Filter Tabs */}
      {availableStatuses.length > 0 && (
        <Box sx={{ borderBottom: 1, borderColor: 'divider', marginBottom: 3 }}>
          <Tabs 
            value={selectedStatus} 
            onChange={handleStatusFilterChange}
            variant="scrollable"
            scrollButtons="auto"
            aria-label="order status filter"
          >
            {availableStatuses.map(group => {
              const groupOrders = sortOrdersByDateDesc(orders.filter(order => statusGroups[group].includes(order.status)));
              return (
                <Tab 
                  key={group}
                  data-testid={`status-tab-${group.toLowerCase()}`}
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {group}
                      <Chip 
                        label={groupOrders.length} 
                        size="small" 
                        color={group === selectedStatus ? 'primary' : 'default'}
                      />
                    </Box>
                  }
                  value={group}
                />
              );
            })}
          </Tabs>
        </Box>
      )}

      { error ? (<Alert severity="error">Sorry, unexpected error occurred</Alert>) : 
      
      filteredOrders?.map(order => (
        <Card
          key={order.id}
          variant="outlined"
          sx={{
            marginBottom: 2,
            borderRadius: 1,
            borderColor: 'divider',
            boxShadow: 'none',
            overflow: 'hidden'
          }}
        >
          <CardContent sx={{ p: 2.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1.5, flexWrap: 'wrap', mb: 2 }}>
              <Typography variant="h6" component="div" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <BookmarkBorderIcon fontSize="small" /> Order # {order.orderNumber}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                <Chip
                  icon={<QueryBuilderOutlinedIcon />}
                  label={getDisplayName(order.status)}
                  color={getStatusChipColor(order.status)}
                  size="small"
                />
                <Chip
                  label={orderTimes?.find(oTime => oTime.id === order.id)?.difference || 'Just now'}
                  variant="outlined"
                  size="small"
                  id={'order-time-elapsed-' + order.id}
                />
              </Box>
            </Box>

            <Grid container spacing={2}>
              <Grid item xs={12} md={5}>
                <Typography variant="subtitle1" sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <FormatListBulletedIcon fontSize="small" /> Items
                </Typography>
                <Box sx={{ pl: 0.5 }}>
                  {order?.items.map((item) => (
                    <Typography key={`${order.id}-${item.itemName}`} variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                      {item.itemName} <strong>({item.count})</strong>
                    </Typography>
                  ))}
                </Box>
              </Grid>

              <Grid item xs={12} md={4}>
                <Typography variant="subtitle1" sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <FormatListBulletedIcon fontSize="small" /> Details
                </Typography>

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {Utils.isChef(user) ? (
                      <>
                        <PersonIcon fontSize="small" />
                        <CustomerInformation customerId={order.customerId} />
                      </>
                    ) : (
                      <>
                        <CallIcon fontSize="small" />
                        <ChefContactInformation kitchenId={order.kitchenId} />
                      </>
                    )}
                  </Typography>

                  <Typography variant="body2" color="text.secondary">
                    <strong>Order Date:</strong> {Utils.formatISODateToStandard(order.orderDate)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    <strong>Item(s) Subtotal:</strong> ${order.itemsSubTotal?.toFixed(2) ?? order.totalAmount.toFixed(2)}
                  </Typography>
                  
                  {order.deliveryFee > 0 && (
                    <>
                      <Typography variant="body2" color="text.secondary">
                        <strong>Delivery Fee:</strong> ${order.deliveryFee.toFixed(2)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontSize: '0.75rem', marginLeft: '0px' }}>
                        {order.deliveryDistanceMiles?.toFixed(2)} miles × ${(order.deliveryFee / (order.deliveryDistanceMiles || 1)).toFixed(2)}/mile
                      </Typography>
                    </>
                  )}
                  <Typography variant="body2" color="text.secondary">
                    <strong>Grand Total:</strong> ${order.totalAmount.toFixed(2)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" data-testid={`order-payment-info-${order.id}`}>
                    <strong>Payment:</strong> {formatPaymentPreference(order.paymentPreference)} ({formatPaymentMethod(order.paymentMethod)})
                  </Typography>
                  {needsPaymentMessage(order) && (
                    <Typography
                      variant="body2"
                      sx={{ color: 'warning.main' }}
                      data-testid={`order-payment-message-${order.id}`}
                    >
                      {getUpfrontPaymentMessage(order)}
                    </Typography>
                  )}
                </Box>
              </Grid>

              <Grid item xs={12} md={3}>
                <Typography variant="subtitle1" sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <LocalShippingOutlinedIcon fontSize="small" /> {order.deliveryMethod.toUpperCase()}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  <strong>Address:</strong> {order.deliveryPickupAtAddress.street} {order.deliveryPickupAtAddress.city}, {order.deliveryPickupAtAddress.state} {order.deliveryPickupAtAddress.zipCode}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
                  <strong>Order Ready By:</strong> {
                    order.orderReadyBy
                      ? Utils.formatISODateToStandard(order.orderReadyBy) || 'Not selected'
                      : 'Not selected'
                  }
                </Typography>
              </Grid>
            </Grid>

            {order.currentOptions && order.currentOptions?.length > 0 && (
              <Grid container spacing={1.5} sx={{ mt: 2, pt: 2, borderTop: 1, borderColor: 'divider' }}>
                {order.currentOptions.map((orderOption) => (
                  <Grid item key={`${order.id}-${orderOption.option}`}>
                    {orderOption.statusAssociated ? (
                      <Button
                        variant="contained"
                        color="primary"
                        sx={{ textTransform: 'none', borderRadius: 2 }}
                        onClick={() => handleOrderAction(orderOption, order)}
                      >
                        {orderOption.option}
                      </Button>
                    ) : (
                      <Button
                        variant="contained"
                        color="inherit"
                        sx={{ textTransform: 'none', borderRadius: 2 }}
                        onClick={() => handleOrderAction(orderOption, order)}
                      >
                        {orderOption.option}
                      </Button>
                    )}
                  </Grid>
                ))}
              </Grid>
            )}
          </CardContent>
        </Card>
      ))

      }

      {/* Empty state when no orders match the filter */}
      {filteredOrders?.length === 0 && !error && (
        <Card variant="outlined" sx={{ padding: 4, textAlign: 'center' }}>
          <Typography variant="h6" color="text.secondary">
            No {selectedStatus} orders found
          </Typography>
        </Card>
      )}
      
      {/* Cancel Order Confirmation Dialog */}
      <Dialog
        open={openConfirmDialog}
        onClose={handleCancelConfirmDialog}
        maxWidth="sm"
        fullWidth
        data-testid="cancel-order-confirmation-dialog"
      >
        <DialogTitle>{pendingAction?.option || 'Cancel'} Order?</DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 2 }}>
            Are you sure you want to {getConfirmationActionVerb(pendingAction?.option)} order <strong>#{pendingOrder?.orderNumber}</strong>? This action cannot be undone.
          </Typography>
          {pendingOrder?.status !== OrderStatus.Placed && (
            <Typography sx={{ color: 'warning.main', fontWeight: 500 }}>
              ⚠️ This order has been accepted or is in progress. Cancelling it may negatively impact your reputation as a {userTypeLabel}.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleCancelConfirmDialog}
            variant="outlined"
            sx={{ textTransform: 'none', textDecoration: 'none' }}
            data-testid="cancel-dialog-no-button"
          >
            No, Keep It
          </Button>
          <Button
            onClick={handleConfirmCancelOrder}
            variant="contained"
            color="error"
            sx={{ textTransform: 'none', textDecoration: 'none' }}
            data-testid="cancel-dialog-yes-button"
          >
            Yes, {pendingAction?.option || 'Cancel'} Order
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}